import type { CSSProperties } from "react";

// Shared Recharts axis-grid/tick/tooltip styling — previously duplicated
// verbatim across all 8 chart-rendering files in this folder (CurvesEditor/
// TimeSeriesEditor/TransectsEditor/PatternsEditor/StatisticsReportDialog/
// LongitudinalProfileDialog/ResultsDashboardCategoryBlock/
// ResultsDashboardMiniChart). `CHART_MUTED` stays a plain hex rather than a
// `var(--color-...)` token like `CHART_TOOLTIP_STYLE` below uses — this app
// has no dedicated "muted chart axis" CSS variable yet, and introducing one
// is a separate design decision from this dedup.
const CHART_MUTED = "#8a97a3";

/**
 * Categorical color palette for chart series — Okabe–Ito
 * (https://jfly.uni-koeln.de/color/), the same colorblind-safe set the map's
 * own `mapTheme.ts` was homogenized to (audit item 31). First 8 are Okabe–Ito;
 * `#8C564B` and `#7F7F7F` extend it to 10 (matplotlib's own "tab10" convention
 * for the same problem) since a real model can add more than 8 elements to
 * one chart at once. Fixed, not theme-reactive — same reasoning as
 * mapTheme.ts's own header: this is data-series identity, not app chrome.
 *
 * Second-audit finding: `ResultsDashboardCategoryBlock.tsx` used to declare
 * this locally as `ELEMENT_COLORS`, while every OTHER chart-rendering file
 * hardcoded a different ad-hoc blue (`#2f8fd1`) that wasn't in this palette
 * at all — four distinct "primary series blue" values coexisted across the
 * product. Lifted here so the whole chart layer sits inside one palette.
 */
export const CHART_PALETTE = [
  "#E69F00", // orange
  "#56B4E9", // sky blue
  "#009E73", // bluish green
  "#0072B2", // blue
  "#D55E00", // vermillion
  "#CC79A7", // reddish purple
  "#000000", // black
  "#F0E442", // yellow
  "#8C564B", // brown (extension)
  "#7F7F7F", // gray (extension)
] as const;

/**
 * Canonical single "primary series" color for the many charts that render
 * one (Curves, Time Series, Patterns bars, Statistics scatter, Transects,
 * Results-dashboard mini chart, the Longitudinal Profile's water-surface
 * line). Okabe-Ito blue — the same blue `mapTheme.ts`'s `LINK_INK` and
 * `CHART_PALETTE[3]` use — so the chart, map and results-dashboard all
 * share one canonical "blue = the data" identity instead of the three
 * near-identical blues the second-audit review found (Tailwind-ish
 * `#2f8fd1`, `--color-primary`, `LINK_INK`).
 */
export const CHART_SERIES_PRIMARY = CHART_PALETTE[3];

/**
 * Longitudinal profile's own three-line stack: earth-ground on top, invert
 * (pipe bottom) below, water surface flowing above the invert when a
 * simulation has run. These are SEMANTIC domain colors (brown = soil, dark
 * = pipe infrastructure, blue = water), NOT positions in a categorical
 * palette — a viewer needs to read the ground line as ground and the water
 * line as water without a legend, so their hues carry meaning that a rotating
 * position-based color scheme would erase. `WATER` reuses
 * `CHART_SERIES_PRIMARY` so it aligns with the canonical "data blue" the
 * rest of the chart layer uses; `GROUND` and `INVERT` stay their own
 * earthy hues (kept from the pre-audit values) for that same domain
 * reason.
 */
export const CHART_LONGITUDINAL_PROFILE = {
  ground: "#8a6d3b",
  invert: "#34495e",
  water: CHART_SERIES_PRIMARY,
} as const;

/**
 * Interior gridlines (audit finding: the previous `#8a97a3` @ 0.3 was
 * barely visible against the dark-mode surface — a "chart with no grid
 * at all" reading in real dark screenshots). Switched to the theme's own
 * `--color-border` token so the same rule tracks light/dark automatically,
 * bumped to 0.5 opacity for a legible minor line, and kept the dashed
 * pattern so it still reads as "helper" against the solid axis border
 * (`CHART_AXIS_LINE` below) — the "major vs. minor" separation the audit
 * asked for, without Recharts having to compute a whole second tier of
 * minor ticks (which its API doesn't natively support).
 */
export const CHART_GRID_PROPS = {
  strokeDasharray: "3 3",
  stroke: "var(--color-border)",
  strokeOpacity: 0.5,
} as const;

/**
 * The solid line that Recharts draws AT the axis boundary — passed as
 * `axisLine` on every `<XAxis>`/`<YAxis>` (see LongitudinalProfileDialog/
 * ResultsDashboardMiniChart callers). Full-opacity `--color-border` so it
 * frames the plot area cleanly as the "major" line, distinct from the
 * dashed interior grid above.
 */
export const CHART_AXIS_LINE = {
  stroke: "var(--color-border)",
} as const;

/** Passed as `tick` to every chart's `<XAxis>`/`<YAxis>`. */
export const CHART_AXIS_TICK = { fontSize: 10, fill: CHART_MUTED } as const;

/** Passed as `<Tooltip>`'s own `contentStyle`. */
export const CHART_TOOLTIP_STYLE: CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius)",
  color: "var(--color-text)",
  fontSize: "0.8rem",
};
